"use client";

import { useUrlFilters } from "@/hooks/use-url-filters";
import { useUserLocation } from "@/hooks/use-user-location";
import { useMap } from "@vis.gl/react-google-maps";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * Automatically centers the map on the user's location if:
 * 1. Permission is granted
 * 2. Location is available
 * 3. User hasn't selected a specific location/project
 * 4. We haven't auto-centered yet (run once)
 * 5. The URL did not initially have a 'center' parameter (meaning user landed on default city view)
 */
export function AutoCenterUser() {
	const map = useMap();
	const { userLocation, permissionStatus } = useUserLocation();
	const { filters } = useUrlFilters();
	const searchParams = useSearchParams();
	const hasCenteredRef = useRef(false);

	// Check if the URL had 'center' parameter on initial load
	// We use a ref to persist this check regardless of subsequent URL updates (e.g. by sync hooks)
	const initialCenterParamRef = useRef(searchParams?.has("center") ?? false);

	useEffect(() => {
		// Only auto-center if we haven't done it yet, location is available,
		// and the user hasn't selecting a specific location entity (like a project or locality)
		// and they didn't explicitly provide coordinates in the URL
		if (
			map &&
			!hasCenteredRef.current &&
			permissionStatus === "granted" &&
			userLocation &&
			!filters.selectedLocationId &&
			(!filters.locations || filters.locations.length === 0) &&
			!initialCenterParamRef.current
		) {
			map.panTo(userLocation);
			map.setZoom(14); // Same zoom level as "Locate Me" button
			hasCenteredRef.current = true;
		}
	}, [map, userLocation, permissionStatus, filters.selectedLocationId, filters.locations]);

	return null;
}
